import React, { Suspense, useEffect } from 'react';
import { useSelector } from 'react-redux';
import CssBaseline from '@mui/material/CssBaseline';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { LanguageProvider } from '@scope/scope-one-ui/core';

import { FullPageLoader, SnackbarNotifications } from 'ui';
import { AuthenticatedApp, UnauthenticatedApp } from 'modules/common';
import { redirectToRootAddress } from 'shared/functions';
import { theme } from 'theme';
import { usePrevious, useFeatures } from 'shared/hooks';
import { logVersion } from 'utils/logVersion';

const App = () => {
  useEffect(logVersion, []);
  const { data: authData } = useSelector(({ auth }) => auth);
  const previousAuthData = usePrevious(authData);
  const { redirectHomeToPageOne } = useFeatures();

  if (redirectHomeToPageOne && ['', '/', '/ca', '/ca/'].includes(window.location.pathname)) {
    window.location.href = `${window.location.origin}/page-one`;

    return null;
  }

  const lastLocation = localStorage.getItem('lastLocation');

  if (process.env.NODE_ENV === 'production' && !authData) {
    redirectToRootAddress();

    return null;
  }

  if (!authData && previousAuthData) {
    window.location.href = '/';
  }

  if (lastLocation) {
    localStorage.removeItem('lastLocation');
    window.location.href = lastLocation;
  }

  return (
    <LanguageProvider>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <CssBaseline />
            <Suspense fallback={<FullPageLoader />}>
              {authData ? <AuthenticatedApp /> : <UnauthenticatedApp />}
            </Suspense>
            <SnackbarNotifications />
          </LocalizationProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </LanguageProvider>
  );
};

export default App;
